import React, { useState } from 'react'
import { router, usePage, useForm } from '@inertiajs/react'
import {
  Box, Button, TextField, Select, MenuItem, FormControl, InputLabel,
  FormControlLabel, Switch, Paper, Typography, Grid, Chip,
} from '@mui/material'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import TimeLapseCalendar from '../../../components/ui/TimeLapseCalendar'
import AppShell from '../../../components/layout/AppShell'
import PageHeader from '../../../components/ui/PageHeader'

Form.layout = (page) => <AppShell>{page}</AppShell>

const MONTH_OPTIONS = [
  { value: '1', label: 'Januar' }, { value: '2', label: 'Februar' },
  { value: '3', label: 'März' }, { value: '4', label: 'April' },
  { value: '5', label: 'Mai' }, { value: '6', label: 'Juni' },
  { value: '7', label: 'Juli' }, { value: '8', label: 'August' },
  { value: '9', label: 'September' }, { value: '10', label: 'Oktober' },
  { value: '11', label: 'November' }, { value: '12', label: 'Dezember' },
]

function TimeLapseSection({ property, tenant }) {
  const [targetDate, setTargetDate] = useState('')
  const today = new Date().toISOString().split('T')[0]

  const handleActivate = () => {
    if (!targetDate) return
    router.patch(`/m/${tenant.slug}/properties/${property.id}/time_lapse`, {
      simulated_date: today,
      target_date: targetDate,
    })
  }

  const handleDeactivate = () => {
    router.patch(`/m/${tenant.slug}/properties/${property.id}/time_lapse`, {
      simulated_date: '',
    })
  }

  return (
    <Paper elevation={0} sx={{ p: 3, mb: 2, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>Zeitraffer</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Simuliert den Zeitverlauf Tag für Tag und führt tägliche Aufgaben aus.
      </Typography>
      {property.simulated_date ? (
        <TimeLapseCalendar
          tenantSlug={tenant.slug}
          propertyId={property.id}
          simulatedDate={property.simulated_date}
          onComplete={handleDeactivate}
        />
      ) : (
        <Box data-testid="time-lapse-setup">
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                type="date"
                label="Zieldatum"
                size="small"
                fullWidth
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                slotProps={{
                  inputLabel: { shrink: true },
                  htmlInput: { 'data-testid': 'time-lapse-target-date', form: '__none__' },
                }}
              />
            </Grid>
          </Grid>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AccessTimeIcon />}
            onClick={handleActivate}
            disabled={!targetDate}
            data-testid="time-lapse-enable"
          >
            Zeitraffer starten
          </Button>
        </Box>
      )}
    </Paper>
  )
}

export default function Form({ property, weg_properties = [], action, errors: serverErrors = {}, time_lapse_enabled = false }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const isEdit = action === 'edit'

  const { data, setData, post, put, processing, errors } = useForm({
    reference: property.reference || '',
    name: property.name || '',
    management_type: property.management_type || 'weg',
    management_start: property.management_start || '',
    management_end: property.management_end || '',
    active: property.active !== false,
    creditor_id: property.creditor_id || '',
    fiscal_year_start: property.fiscal_year_start || '1',
    vat_status: property.vat_status || 'exempt',
    vat_rate: property.vat_rate || '',
    address: {
      street: property.address?.street || '',
      house_number: property.address?.house_number || '',
      postal_code: property.address?.postal_code || '',
      city: property.address?.city || '',
      country: property.address?.country || 'DE',
    },
  })

  const allErrors = { ...serverErrors, ...errors }
  const isWeg = data.management_type === 'weg'
  const isRental = ['mv', 'sev'].includes(data.management_type)
  const isSev = data.management_type === 'sev'

  const handleSubmit = (e) => {
    e.preventDefault()
    if (isEdit) {
      put(`/m/${tenant.slug}/properties/${property.id}`)
    } else {
      post(`/m/${tenant.slug}/properties`)
    }
  }

  const breadcrumbs = isEdit
    ? [{ label: 'Übersicht', href: `/m/${tenant.slug}/properties/${property.id}` }, { label: 'Bearbeiten' }]
    : [{ label: 'Immobilien', href: `/m/${tenant.slug}/properties` }, { label: 'Neue Immobilie' }]

  return (
    <Box>
      <PageHeader
        title={isEdit ? `${property.name} bearbeiten` : 'Neue Immobilie'}
        breadcrumbs={breadcrumbs}
      />

      <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 600 }}>
        {/* Stammdaten */}
        <Paper elevation={0} sx={{ p: 3, mb: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Stammdaten</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Referenz"
                required
                fullWidth
                size="small"
                value={data.reference}
                onChange={(e) => setData('reference', e.target.value)}
                error={!!allErrors.reference}
                helperText={allErrors.reference?.[0]}
                slotProps={{ htmlInput: { 'data-testid': 'reference-input' } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Name"
                required
                fullWidth
                size="small"
                value={data.name}
                onChange={(e) => setData('name', e.target.value)}
                error={!!allErrors.name}
                helperText={allErrors.name?.[0]}
                slotProps={{ htmlInput: { 'data-testid': 'name-input' } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth size="small" required error={!!allErrors.management_type}>
                <InputLabel>Verwaltungsart</InputLabel>
                <Select
                  value={data.management_type}
                  label="Verwaltungsart"
                  onChange={(e) => setData('management_type', e.target.value)}
                  data-testid="management-type-select"
                >
                  <MenuItem value="weg">WEG</MenuItem>
                  <MenuItem value="mv">MV</MenuItem>
                  <MenuItem value="sev">SEV</MenuItem>
                  <MenuItem value="privat">Privat</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Verwaltungsbeginn"
                type="date"
                required
                fullWidth
                size="small"
                value={data.management_start}
                onChange={(e) => setData('management_start', e.target.value)}
                error={!!allErrors.management_start}
                helperText={allErrors.management_start?.[0]}
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { 'data-testid': 'management-start-input' } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Verwaltungsende"
                type="date"
                fullWidth
                size="small"
                value={data.management_end}
                onChange={(e) => setData('management_end', e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={data.active}
                    onChange={(e) => setData('active', e.target.checked)}
                    data-testid="active-toggle"
                  />
                }
                label="Aktiv"
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Adresse */}
        <Paper elevation={0} sx={{ p: 3, mb: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>Adresse</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Straße"
                fullWidth
                size="small"
                value={data.address.street}
                onChange={(e) => setData('address', { ...data.address, street: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Hausnummer"
                fullWidth
                size="small"
                value={data.address.house_number}
                onChange={(e) => setData('address', { ...data.address, house_number: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="PLZ"
                fullWidth
                size="small"
                value={data.address.postal_code}
                onChange={(e) => setData('address', { ...data.address, postal_code: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Ort"
                fullWidth
                size="small"
                value={data.address.city}
                onChange={(e) => setData('address', { ...data.address, city: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Land"
                fullWidth
                size="small"
                value={data.address.country}
                onChange={(e) => setData('address', { ...data.address, country: e.target.value })}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* WEG-spezifisch */}
        {isWeg && (
          <Paper elevation={0} sx={{ p: 3, mb: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>WEG-spezifisch</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Gläubiger-ID"
                  fullWidth
                  size="small"
                  value={data.creditor_id}
                  onChange={(e) => setData('creditor_id', e.target.value)}
                  error={!!allErrors.creditor_id}
                  helperText={allErrors.creditor_id?.[0] || 'Optional – SEPA Creditor ID (z.B. DE98ZZZ09999999999)'}
                  slotProps={{ htmlInput: { 'data-testid': 'creditor-id-input' } }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Geschäftsjahr-Beginn</InputLabel>
                  <Select
                    value={data.fiscal_year_start}
                    label="Geschäftsjahr-Beginn"
                    onChange={(e) => setData('fiscal_year_start', e.target.value)}
                    data-testid="fiscal-year-start-select"
                  >
                    {MONTH_OPTIONS.map((m) => (
                      <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* MV/SEV-spezifisch */}
        {isRental && (
          <Paper elevation={0} sx={{ p: 3, mb: 2, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {isSev ? 'SEV-spezifisch' : 'MV-spezifisch'}
            </Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>USt-Status</InputLabel>
                  <Select
                    value={data.vat_status}
                    label="USt-Status"
                    onChange={(e) => setData('vat_status', e.target.value)}
                  >
                    <MenuItem value="exempt">Steuerfrei</MenuItem>
                    <MenuItem value="full">Steuerpflichtig (voll)</MenuItem>
                    <MenuItem value="partial">Steuerpflichtig (teilweise)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {data.vat_status !== 'exempt' && (
                <Grid size={{ xs: 12 }}>
                  <TextField
                    label="USt-Satz (%)"
                    fullWidth
                    size="small"
                    type="number"
                    value={data.vat_rate}
                    onChange={(e) => setData('vat_rate', e.target.value)}
                  />
                </Grid>
              )}
            </Grid>

            {/* SEV parent WEG */}
            {isSev && weg_properties.length > 0 && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    SEV-Zuordnung (optional: übergeordnete WEG-Immobilie)
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Paper>
        )}

        {/* Time Lapse — only on edit when feature flag enabled */}
        {isEdit && time_lapse_enabled && (
          <TimeLapseSection property={property} tenant={tenant} />
        )}

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={processing}
            data-testid="submit-btn"
          >
            {processing ? 'Wird gespeichert...' : 'Speichern'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => router.visit(`/m/${tenant.slug}/properties`)}
          >
            Abbrechen
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
